import {useState} from 'react'
import styled, {css, keyframes} from 'styled-components'
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Menu,
    MenuItem,
    Tooltip,
} from '@mui/material'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined'
import {getCurrentColors} from '../../utils/colors'
import {
    DocType,
    DriveFile,
    DriveNode,
    PineconeIndex,
    SYNC_CONFIG,
    SyncStatus,
} from './mockData'

const COLORS = getCurrentColors()

const spin = keyframes`
    to {
        transform: rotate(360deg);
    }
`

export const SyncPill = styled.span<{status: SyncStatus}>`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-weight: 600;
    padding: 3px 9px;
    border-radius: 20px;
    color: ${({status}: {status: SyncStatus}) => SYNC_CONFIG[status].color};
    background: ${({status}: {status: SyncStatus}) =>
        SYNC_CONFIG[status].wash};

    svg {
        font-size: 13px;
        ${({status}: {status: SyncStatus}) =>
            status === 'run' &&
            css`
                animation: ${spin} 1s linear infinite;
            `}
    }
`

const FILE_TYPE_COLORS: Record<DocType, {color: string; bg: string}> = {
    PDF: {color: '#cf3434', bg: '#fdecec'},
    DOCX: {color: COLORS.primariaEscura, bg: '#eaf3fd'},
    XLSX: {color: '#0e8f78', bg: '#e7f8f3'},
    TXT: {color: '#5d6b72', bg: '#f2f5f6'},
}

export const FileTypeBadge = styled.span<{type: DocType}>`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: grid;
    place-items: center;
    flex-shrink: 0;
    font-size: 9px;
    font-weight: 700;
    color: ${({type}: {type: DocType}) => FILE_TYPE_COLORS[type].color};
    background: ${({type}: {type: DocType}) => FILE_TYPE_COLORS[type].bg};
`

export const PendingTag = styled.span<{variant: 'upload' | 'remove'}>`
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 2px 7px;
    border-radius: 20px;
    margin-left: 8px;
    color: ${({variant}) => (variant === 'upload' ? '#1769c4' : '#cf3434')};
    background: ${({variant}) =>
        variant === 'upload' ? '#eaf3fd' : '#fdecec'};
`

export const IndexChip = styled.span`
    display: inline-flex;
    font-size: 10px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 20px;
    background: ${COLORS.primaria};
    color: ${COLORS.primariaEscura};
    white-space: nowrap;
`

interface AssociateIndexMenuProps {
    indexes: PineconeIndex[]
    selectedIds: string[]
    onToggle: (indexId: string) => void
}

// Botão + menu de checkboxes para associar um documento a um ou mais
// índices dessa mesma KB. Aplica na hora (não depende do "Atualizar").
export function AssociateIndexMenu({
    indexes,
    selectedIds,
    onToggle,
}: AssociateIndexMenuProps) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

    return (
        <>
            <Tooltip title="Associar a um índice">
                <IconButton
                    size="small"
                    aria-label="Associar a um índice"
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                >
                    <BookmarkAddOutlinedIcon sx={{fontSize: 15}} />
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
            >
                {indexes.length === 0 && (
                    <MenuItem disabled>Nenhum índice criado ainda</MenuItem>
                )}
                {indexes.map((idx) => (
                    <MenuItem
                        key={idx.id}
                        dense
                        onClick={() => onToggle(idx.id)}
                        sx={{gap: '4px'}}
                    >
                        <Checkbox
                            size="small"
                            checked={selectedIds.includes(idx.id)}
                            sx={{padding: '4px'}}
                        />
                        {idx.name}
                    </MenuItem>
                ))}
            </Menu>
        </>
    )
}

function extToDocType(fileName: string): DocType {
    const ext = fileName.split('.').pop()?.toLowerCase()
    if (ext === 'pdf') return 'PDF'
    if (ext === 'docx' || ext === 'doc') return 'DOCX'
    if (ext === 'xlsx' || ext === 'xls') return 'XLSX'
    return 'TXT'
}

function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function filesToDriveFiles(files: FileList): DriveFile[] {
    return Array.from(files).map((file, i) => ({
        id: `up-${Date.now()}-${i}`,
        kind: 'file' as const,
        name: file.name,
        type: extToDocType(file.name),
        size: formatBytes(file.size),
        pending: 'upload' as const,
        indexIds: [],
    }))
}

const DiffSectionTitle = styled.div`
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #8a969c;
    margin-bottom: 8px;
`

const DiffList = styled.ul`
    list-style: none;
    margin: 0 0 16px;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
`

const DiffItem = styled.li<{variant: 'upload' | 'remove'}>`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #33454e;

    svg {
        font-size: 16px;
        flex-shrink: 0;
        color: ${({variant}) => (variant === 'upload' ? '#1769c4' : '#cf3434')};
    }
`

export interface IndexChangeItem {
    fileName: string
    indexName: string
    added: boolean
}

interface ConfirmDialogProps {
    open: boolean
    title: string
    uploads: DriveFile[]
    removals: DriveFile[]
    indexChanges?: IndexChangeItem[]
    onCancel: () => void
    onConfirm: () => void
    confirmLabel?: string
}

export function ConfirmDialog({
    open,
    title,
    uploads,
    removals,
    indexChanges = [],
    onCancel,
    onConfirm,
    confirmLabel = 'Confirmar',
}: ConfirmDialogProps) {
    const isEmpty =
        uploads.length === 0 &&
        removals.length === 0 &&
        indexChanges.length === 0

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
            <DialogTitle sx={{fontWeight: 700, fontSize: '18px'}}>
                {title}
            </DialogTitle>
            <DialogContent>
                {isEmpty && (
                    <p style={{color: '#8a969c', fontSize: 13, margin: 0}}>
                        Nenhuma alteração pendente.
                    </p>
                )}
                {uploads.length > 0 && (
                    <>
                        <DiffSectionTitle>
                            Serão enviados ({uploads.length})
                        </DiffSectionTitle>
                        <DiffList>
                            {uploads.map((d) => (
                                <DiffItem key={d.id} variant="upload">
                                    <UploadFileOutlinedIcon />
                                    {d.name}
                                </DiffItem>
                            ))}
                        </DiffList>
                    </>
                )}
                {removals.length > 0 && (
                    <>
                        <DiffSectionTitle>
                            Serão removidos ({removals.length})
                        </DiffSectionTitle>
                        <DiffList>
                            {removals.map((d) => (
                                <DiffItem key={d.id} variant="remove">
                                    <DeleteOutlineOutlinedIcon />
                                    {d.name}
                                </DiffItem>
                            ))}
                        </DiffList>
                    </>
                )}
                {indexChanges.length > 0 && (
                    <>
                        <DiffSectionTitle>
                            Associações de índice ({indexChanges.length})
                        </DiffSectionTitle>
                        <DiffList>
                            {indexChanges.map((c, i) => (
                                <DiffItem
                                    key={i}
                                    variant={c.added ? 'upload' : 'remove'}
                                >
                                    <BookmarkAddOutlinedIcon />
                                    {c.fileName} → {c.indexName} (
                                    {c.added ? 'associar' : 'desassociar'})
                                </DiffItem>
                            ))}
                        </DiffList>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{padding: '0 24px 20px'}}>
                <Button onClick={onCancel} sx={{textTransform: 'none'}}>
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    disabled={isEmpty}
                    onClick={onConfirm}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '9px',
                    }}
                >
                    {confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

const TreeRow = styled.div<{depth: number; tone: 'normal' | 'upload' | 'remove' | 'selected'}>`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 10px;
    padding-left: ${({depth}) => 8 + depth * 20}px;
    border-radius: 8px;

    background: ${({tone}) =>
        tone === 'upload'
            ? '#eaf3fd'
            : tone === 'remove'
              ? '#fdecec'
              : tone === 'selected'
                ? '#f2f5f6'
                : 'transparent'};

    &:hover {
        background: ${({tone}) => (tone === 'remove' ? '#fbdede' : '#f2f5f6')};
    }
`

const TreeChevronBtn = styled.button<{empty?: boolean}>`
    border: none;
    background: none;
    padding: 2px;
    border-radius: 5px;
    display: grid;
    place-items: center;
    color: #8a969c;
    cursor: pointer;
    visibility: ${({empty}) => (empty ? 'hidden' : 'visible')};

    svg {
        font-size: 16px;
    }
`

const TreeLabel = styled.span<{struck?: boolean}>`
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13.5px;
    color: #33454e;
    text-decoration: ${({struck}) => (struck ? 'line-through' : 'none')};
    opacity: ${({struck}) => (struck ? 0.6 : 1)};

    span.name {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`

const TreeMeta = styled.span`
    font-size: 11px;
    color: #8a969c;
    flex-shrink: 0;
    margin-right: 6px;
`

const TreeActions = styled.div`
    display: flex;
    gap: 2px;
    flex-shrink: 0;

    button {
        border: none;
        background: none;
        cursor: pointer;
        padding: 5px;
        border-radius: 6px;
        display: grid;
        place-items: center;

        &:hover {
            background: #e6eaec;
        }

        svg {
            font-size: 15px;
        }
    }
`

interface DriveTreeProps {
    node: DriveNode
    depth?: number
    expandedIds: Set<string>
    onToggleExpand: (id: string) => void
    selectedFolderId: string | null
    onSelectFolder: (id: string) => void
    onToggleRemove: (file: DriveFile) => void
    indexes: PineconeIndex[]
    onToggleIndex: (file: DriveFile, indexId: string) => void
}

export function DriveTree({
    node,
    depth = 0,
    expandedIds,
    onToggleExpand,
    selectedFolderId,
    onSelectFolder,
    onToggleRemove,
    indexes,
    onToggleIndex,
}: DriveTreeProps) {
    if (node.kind === 'file') {
        const tone = node.pending === 'upload' ? 'upload' : node.pending === 'remove' ? 'remove' : 'normal'
        return (
            <TreeRow depth={depth} tone={tone}>
                <TreeChevronBtn empty aria-hidden>
                    <ChevronRightIcon />
                </TreeChevronBtn>
                <TreeLabel struck={node.pending === 'remove'}>
                    <FileTypeBadge type={node.type}>
                        {node.type}
                    </FileTypeBadge>
                    <span className="name">{node.name}</span>
                    {node.pending === 'upload' && (
                        <PendingTag variant="upload">Será enviado</PendingTag>
                    )}
                    {node.pending === 'remove' && (
                        <PendingTag variant="remove">Será removido</PendingTag>
                    )}
                    {node.indexIds.map((id) => (
                        <IndexChip key={id}>
                            {indexes.find((i) => i.id === id)?.name ?? id}
                        </IndexChip>
                    ))}
                </TreeLabel>
                <TreeMeta>{node.size}</TreeMeta>
                <TreeActions>
                    <button disabled aria-label="Baixar">
                        <DownloadOutlinedIcon />
                    </button>
                    <AssociateIndexMenu
                        indexes={indexes}
                        selectedIds={node.indexIds}
                        onToggle={(indexId) => onToggleIndex(node, indexId)}
                    />
                    <Tooltip
                        title={
                            node.pending === 'upload'
                                ? 'Cancelar envio'
                                : node.pending === 'remove'
                                  ? 'Desfazer remoção'
                                  : 'Remover'
                        }
                    >
                        <button
                            aria-label="Remover"
                            onClick={() => onToggleRemove(node)}
                        >
                            {node.pending === 'upload' ? (
                                <CloseOutlinedIcon />
                            ) : node.pending === 'remove' ? (
                                <RestoreOutlinedIcon />
                            ) : (
                                <DeleteOutlineOutlinedIcon />
                            )}
                        </button>
                    </Tooltip>
                </TreeActions>
            </TreeRow>
        )
    }

    const expanded = expandedIds.has(node.id)
    const isSelected = node.id === selectedFolderId

    return (
        <>
            <TreeRow depth={depth} tone={isSelected ? 'selected' : 'normal'}>
                <TreeChevronBtn
                    aria-label={expanded ? 'Recolher' : 'Expandir'}
                    onClick={() => onToggleExpand(node.id)}
                >
                    {expanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                </TreeChevronBtn>
                <TreeLabel
                    onClick={() => onSelectFolder(node.id)}
                    style={{cursor: 'pointer'}}
                >
                    <FolderOutlinedIcon
                        sx={{fontSize: 18, color: COLORS.primariaEscura}}
                    />
                    <span className="name">{node.name}</span>
                    {isSelected && (
                        <PendingTag variant="upload">
                            Destino do upload
                        </PendingTag>
                    )}
                </TreeLabel>
            </TreeRow>
            {expanded &&
                node.children.map((child) => (
                    <DriveTree
                        key={child.id}
                        node={child}
                        depth={depth + 1}
                        expandedIds={expandedIds}
                        onToggleExpand={onToggleExpand}
                        selectedFolderId={selectedFolderId}
                        onSelectFolder={onSelectFolder}
                        onToggleRemove={onToggleRemove}
                        indexes={indexes}
                        onToggleIndex={onToggleIndex}
                    />
                ))}
        </>
    )
}
